<script lang="ts" setup>
import type { CategoryFilter } from "~~/models/filters";
import type { PropertyFilterDto } from "~~/server/api/ecom/products/search.post";
import type { CatalogPageRecord, LayoutRecord, ProductGroupPromoRecord } from "~~/server/graphql/datocms/generated";
import type { PlanktonCategoryWithSlugs } from "~~/server/plankton";

const { cmsData, category, pageTitle, filter } = defineProps<{
  layout: LayoutRecord;
  cmsData: CatalogPageRecord;
  pageTitle?: string;
  category?: PlanktonCategoryWithSlugs | null;
  filter?: {
    productIdList?: Array<number>;
    propertyFilterList?: Array<PropertyFilterDto>;
  };
  show: {
    categoryDrilldown: boolean;
  };
}>();

const { locale } = useI18n();
const { loggedIn } = useUserSession();

const categories = await useCategories();

const { data: productGroupPromo } = await useFetch("/api/cms/product-groups/promo", {
  query: { locale: locale, categoryId: category?.id },
});

const {
  status: catalogStatus,
  catalog,
  searchState: catalogSearchState,
  activeTitle: catalogActiveTitle,
  pagination: catalogPagination,
} = useCatalog(
  {
    initialize: {
      searchState: true,
      catalog: true,
      ssr: !loggedIn.value,
      scrollIntoViewThreshold: 500,
    },
    pageTitle: pageTitle,
    params: {
      category: category,
      productIdList: filter?.productIdList,
      propertyFilterList: filter?.propertyFilterList,
      pageSize: { value: defaultCatalogPageSize, modifier: productGroupPromo.value ? -2 : 0 },
    },
  },
);

const {
  status: contentSearchStatus,
  searchState: contentSearchSearchState,
  activeTitle: contentSearchActiveTitle,
  pagination: contentSearchPagination,
} = useCustomContentSearch({
  initialize: true,
});

const activeTitle = computed(() => catalogActiveTitle.value ?? contentSearchActiveTitle.value);

const selectedSearchTab: Ref<"catalog" | "content"> = ref("catalog");

watchEffect(() => {
  if (catalogStatus.value === "success"
    && contentSearchStatus.value === "success"
    && catalogPagination.value.totalItems === 0
    && contentSearchPagination.value.totalItems > 0) {
    selectedSearchTab.value = "content";
  }
});

const categoryFilters = computed(() => {
  const levelCategoryList: Array<{ level: number; categoryIdList: Array<number> }> = [];

  for (const categoryItem of categories.tree.getAll()) {
    const levelCategory = levelCategoryList.find((lc: any) => lc.level === categoryItem.level);

    if (levelCategory === null || levelCategory === undefined) {
      levelCategoryList.push({ level: categoryItem.level, categoryIdList: [categoryItem.id] });
    }
    else {
      levelCategory.categoryIdList.push(categoryItem.id);
    }
  }

  const currentLevel = categories.tree.getCategory(category?.id ?? 0)?.level ?? 1;
  const upperLevelCategory = levelCategoryList.find((lc: any) => lc.level === currentLevel + 1);

  return catalogSearchState.value.categoryFilters
    .filter((cf: CategoryFilter) => {
      return upperLevelCategory?.categoryIdList.includes(cf.categoryId) ?? false;
    })
    .sort((a: CategoryFilter, b: CategoryFilter) => {
      return b.productCount - a.productCount;
    });
});

useHead({
  title: () => activeTitle.value || null,
});
</script>

<template>
  <div>
    <ProductCatalogHeader view-mode="list" :cms-data="cmsData as CatalogPageRecord" :category-tree="categories.tree" :category="category" />

    <div id="scroll-into-view" class="my-lg gap-md flex flex-col">
      <div class="gap-2xl flex flex-col">
        <div class="gap-lg flex flex-col">
          <h1>{{ activeTitle }}</h1>

          <div v-if="catalogSearchState.term?.length || contentSearchSearchState.term?.length" class="gap-lg flex">
            <a
              href="#" class="font-semibold"
              :class="{ 'text-primary underline underline-offset-8': selectedSearchTab === 'catalog' }"
              @click.prevent="selectedSearchTab = 'catalog'"
            >
              {{ cmsData.overviewTitleCatalog }} ({{ catalogPagination.totalItems }})
            </a>
            <a
              href="#" class="font-semibold"
              :class="{ 'text-primary underline underline-offset-8': selectedSearchTab === 'content' }"
              @click.prevent="selectedSearchTab = 'content'"
            >
              {{ cmsData.overviewTitleContent }} ({{ contentSearchPagination.totalItems }})
            </a>
          </div>

          <div v-if="show.categoryDrilldown && catalog.length" v-show="selectedSearchTab === 'catalog'">
            <ProductCategoryDrilldown
              :cms-data="cmsData"
              :category-filters="categoryFilters"
              :category-tree="categories.tree"
              :category="category"
            />
          </div>
        </div>

        <div v-show="selectedSearchTab === 'catalog'">
          <ProductSearchFiltersDesktop :cms-data="cmsData as CatalogPageRecord" />
          <ProductSearchFiltersMobile :cms-data="cmsData as CatalogPageRecord" />
        </div>
      </div>

      <div v-show="selectedSearchTab === 'catalog'">
        <ProductSearchResults
          :layout="layout"
          :cms-data="cmsData"
          :category="category"
          :product-group-promo="productGroupPromo as ProductGroupPromoRecord"
        />
      </div>

      <div v-show="selectedSearchTab === 'content'">
        <ContentSearchResults :cms-data="cmsData" />
      </div>
    </div>
  </div>
</template>

<style scoped>

</style>
